/** Small fetch client for the bd_ai Django API. */

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export function createApi({ baseUrl, prefix, getAuthHeaders, timeoutMs }) {
  const root = `${(baseUrl || "").replace(/\/$/, "")}${prefix}`;

  async function request(path, { method = "GET", body, signal, raw = false } = {}) {
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", onAbort);
    }
    const timer = setTimeout(() => controller.abort("timeout"), timeoutMs || 180000);

    let res;
    try {
      res = await fetch(`${root}${path}`, {
        method,
        headers: {
          Accept: raw ? "*/*" : "application/json",
          ...(body ? { "Content-Type": "application/json" } : {}),
          ...(getAuthHeaders ? getAuthHeaders() : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        // signal: controller.signal,
        // credentials: "include",
      });
    } catch (err) {
      if (controller.signal.aborted) {
        if (signal && signal.aborted) throw new ApiError("Request cancelled", 0);
        throw new ApiError("The request took too long. Try a shorter date range or a simpler question.", 0);
      }
      throw new ApiError("Cannot reach the server. Check your connection.", 0);
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onAbort);
    }

    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const data = await res.json();
        message = data.message || data.detail || message;
      } catch (e) {
        /* not JSON */
      }
      if (res.status === 401 || res.status === 403) message = "You are not logged in or not allowed to use the assistant.";
      throw new ApiError(message, res.status);
    }
    if (raw) return res;
    return res.json();
  }

  return {
    ask: (payload, signal) => request("/ask/", { method: "POST", body: payload, signal }),
    suggestions: () => request("/suggestions/"),
    listConversations: (empCode, q) => {
      const params = new URLSearchParams();
      if (empCode) params.set("emp_code", empCode);
      if (q) params.set("q", q);
      const qs = params.toString();
      return request(`/conversations/${qs ? `?${qs}` : ""}`);
    },
    getConversation: (id) => request(`/conversations/${id}/`),
    updateConversation: (id, data) => request(`/conversations/${id}/`, { method: "PATCH", body: data }),
    deleteConversation: (id) => request(`/conversations/${id}/`, { method: "DELETE" }),
    feedback: (messageId, feedback, note) =>
      request(`/messages/${messageId}/feedback/`, { method: "POST", body: { feedback, note } }),
    exportMessage: async (messageId, fileType) => {
      const res = await request(`/messages/${messageId}/export/?file_type=${fileType}`, { raw: true });
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      return { blob, filename: match ? match[1] : `bd_ai_report.${fileType}` };
    },
  };
}
